<script setup lang="ts">
import { computed, markRaw, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowPathIcon, ChevronLeftIcon, ExclamationTriangleIcon, InboxIcon, PlusIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import AttentionInterlocutorsEditor from '@/components/admin/AttentionInterlocutorsEditor.vue'
import UserSelect from '@/components/admin/UserSelect.vue'
import {
  useAttentionPointsStore,
  type AttentionInterlocutor,
  type AttentionPoint,
  type AttentionPointCriticite,
  type AttentionPointStatus,
  type AttentionPointType,
  type JournalEntryKind,
} from '@/stores/useAttentionPointsStore'
import {
  ATTENTION_CRITICITES,
  ATTENTION_CRITICITE_BY_VALUE,
  ATTENTION_DOMAINES_SUGGESTED,
  ATTENTION_STALE_THRESHOLDS,
  ATTENTION_STATUSES,
  ATTENTION_STATUS_BY_VALUE,
  ATTENTION_TYPES,
  ATTENTION_TYPE_BY_VALUE,
  DEFAULT_STALE_THRESHOLD_DAYS,
  JOURNAL_KINDS,
  JOURNAL_KIND_BY_VALUE,
} from '@/stores/attentionPointMeta'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'

const route = useRoute()
const router = useRouter()
const store = useAttentionPointsStore()
const usersStore = useBoostCoreUsersStore()

const arrowPathIcon = markRaw(ArrowPathIcon)
const chevronLeftIcon = markRaw(ChevronLeftIcon)
const plusIcon = markRaw(PlusIcon)

const TYPES = ATTENTION_TYPES
const CRITICITES = ATTENTION_CRITICITES
const STATUSES = ATTENTION_STATUSES

function todayInput(): string {
  return new Date().toISOString().slice(0, 10)
}
function toDateInput(iso?: string): string {
  return iso ? iso.slice(0, 10) : ''
}
function formatDate(iso?: string): string {
  return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'
}
function formatDateTime(iso?: string): string {
  return iso ? new Date(iso).toLocaleString('fr-FR') : '—'
}
function isOverdue(p: AttentionPoint): boolean {
  return p.status !== 'LEVE' && !!p.resolutionPrevue && new Date(p.resolutionPrevue) < new Date()
}

const THRESHOLDS = ATTENTION_STALE_THRESHOLDS

/**
 * Dernière activité = la plus récente entre la date d'entrée (antidatable) et les
 * entrées de journal (commentaire / détail / action). Les changements de statut
 * ne comptent PAS : passer un point « en cours » n'est pas un suivi qui justifie
 * de ne plus relancer.
 */
function lastActivityAt(p: AttentionPoint): number {
  const baseline = new Date(p.dateEntree ?? p.createdAt).getTime()
  const times = [baseline, ...(p.journal ?? []).map((j) => new Date(j.at).getTime())].filter((t) => !isNaN(t))
  return times.length ? Math.max(...times) : baseline
}
function daysSinceActivity(p: AttentionPoint): number {
  return Math.floor((Date.now() - lastActivityAt(p)) / 86_400_000)
}
/** « Sans suivi » : non levé + délai depuis la dernière activité ≥ seuil du point. */
function isStale(p: AttentionPoint): boolean {
  return p.status !== 'LEVE' && daysSinceActivity(p) >= (p.staleThresholdDays ?? DEFAULT_STALE_THRESHOLD_DAYS)
}
function interlocutorLabel(i: AttentionInterlocutor): string {
  const name = [i.firstName, i.lastName].filter(Boolean).join(' ')
  const base = name || i.email || '—'
  return i.company ? `${base} · ${i.company}` : base
}
function responsableLabel(p: AttentionPoint): string {
  if (!p.responsable) return '—'
  return [p.responsable.firstName, p.responsable.lastName].filter(Boolean).join(' ') || p.responsable.email || '—'
}

/* ------------------------------ Liste / filtres ------------------------------ */
const search = ref('')
const selectedStatuses = ref<AttentionPointStatus[]>(['OUVERT', 'EN_COURS'])
const selectedTypes = ref<AttentionPointType[]>([])
const selectedCriticites = ref<AttentionPointCriticite[]>([])
const overdueOnly = ref(false)

async function refresh() {
  await Promise.all([
    store.fetchAll({
      status: selectedStatuses.value.length ? selectedStatuses.value : undefined,
      type: selectedTypes.value.length ? selectedTypes.value : undefined,
      criticite: selectedCriticites.value.length ? selectedCriticites.value : undefined,
      search: search.value || undefined,
      overdue: overdueOnly.value || undefined,
    }),
    store.fetchStats(),
  ])
}

let refreshTimeout: ReturnType<typeof setTimeout> | undefined
function debouncedRefresh() {
  if (refreshTimeout) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(refresh, 400)
}

async function doExport(format: 'csv' | 'xlsx') {
  await store.exportPoints({
    status: selectedStatuses.value.length ? selectedStatuses.value : undefined,
    type: selectedTypes.value.length ? selectedTypes.value : undefined,
    criticite: selectedCriticites.value.length ? selectedCriticites.value : undefined,
    search: search.value || undefined,
    overdue: overdueOnly.value || undefined,
    format,
  })
}

/* ------------------------------ Création ------------------------------ */
const showCreate = ref(false)
const createForm = reactive({
  title: '',
  type: 'BLOCAGE' as AttentionPointType,
  criticite: 'MOYENNE' as AttentionPointCriticite,
  description: '',
  impact: '',
  dateEntree: todayInput(),
  dateDebut: '',
  resolutionPrevue: '',
  responsableUuid: '',
  domaines: [] as string[],
  staleThresholdDays: DEFAULT_STALE_THRESHOLD_DAYS as number,
  interlocuteurs: [] as AttentionInterlocutor[],
})

const createTypeMeta = computed(() => ATTENTION_TYPE_BY_VALUE[createForm.type])

function interlocuteursValid(list: AttentionInterlocutor[]): boolean {
  if (list.length === 0) return false
  return list.every((i) =>
    i.kind === 'INTERNE'
      ? !!i.userUuid
      : [i.firstName, i.lastName, i.email, i.company].some((v) => v && v.trim().length > 0),
  )
}

const canCreate = computed(
  () => createForm.title.trim().length > 0 && interlocuteursValid(createForm.interlocuteurs) && !store.loading,
)

function openCreate() {
  Object.assign(createForm, {
    title: '',
    type: 'BLOCAGE',
    criticite: 'MOYENNE',
    description: '',
    impact: '',
    dateEntree: todayInput(),
    dateDebut: '',
    resolutionPrevue: '',
    responsableUuid: '',
    domaines: [],
    staleThresholdDays: DEFAULT_STALE_THRESHOLD_DAYS,
    interlocuteurs: [],
  })
  showCreate.value = true
}

async function submitCreate() {
  if (!canCreate.value) return
  const created = await store.create({
    title: createForm.title.trim(),
    type: createForm.type,
    criticite: createForm.criticite,
    description: createForm.description.trim() || undefined,
    impact: createForm.impact.trim() || undefined,
    dateEntree: createForm.dateEntree || undefined,
    dateDebut: createForm.dateDebut || undefined,
    resolutionPrevue: createForm.resolutionPrevue || undefined,
    responsableUuid: createForm.responsableUuid || undefined,
    domaines: createForm.domaines,
    staleThresholdDays: createForm.staleThresholdDays,
    interlocuteurs: createForm.interlocuteurs,
  })
  showCreate.value = false
  if (created?._id) openPoint(created._id)
}

/* ------------------------------ Détail / édition ------------------------------ */
const edit = reactive({
  title: '',
  type: 'AUTRE' as AttentionPointType,
  criticite: 'MOYENNE' as AttentionPointCriticite,
  description: '',
  impact: '',
  dateEntree: '',
  dateDebut: '',
  resolutionPrevue: '',
  responsableUuid: '',
  domaines: [] as string[],
  staleThresholdDays: DEFAULT_STALE_THRESHOLD_DAYS as number,
  interlocuteurs: [] as AttentionInterlocutor[],
})

const editTypeMeta = computed(() => ATTENTION_TYPE_BY_VALUE[edit.type])

function loadEditFromPoint(p: AttentionPoint) {
  edit.title = p.title
  edit.type = p.type
  edit.criticite = p.criticite
  edit.description = p.description
  edit.impact = p.impact
  edit.dateEntree = toDateInput(p.dateEntree)
  edit.dateDebut = toDateInput(p.dateDebut)
  edit.resolutionPrevue = toDateInput(p.resolutionPrevue)
  edit.responsableUuid = p.responsable?.userUuid ?? ''
  edit.domaines = [...(p.domaines ?? [])]
  edit.staleThresholdDays = p.staleThresholdDays ?? DEFAULT_STALE_THRESHOLD_DAYS
  edit.interlocuteurs = (p.interlocuteurs ?? []).map((i) => ({ ...i }))
}

const editValid = computed(() => edit.title.trim().length > 0 && interlocuteursValid(edit.interlocuteurs))

async function saveDetails() {
  if (!store.point || !editValid.value) return
  await store.update(store.point._id, {
    title: edit.title.trim(),
    type: edit.type,
    criticite: edit.criticite,
    description: edit.description,
    impact: edit.impact,
    dateEntree: edit.dateEntree || undefined,
    dateDebut: edit.dateDebut || undefined,
    resolutionPrevue: edit.resolutionPrevue || undefined,
    responsableUuid: edit.responsableUuid || null,
    domaines: edit.domaines,
    staleThresholdDays: edit.staleThresholdDays,
    interlocuteurs: edit.interlocuteurs,
  })
}

/* Domaines : chips suggérés + ajout libre (partagé création/édition via cible). */
function toggleDomaine(target: { domaines: string[] }, d: string) {
  target.domaines = target.domaines.includes(d) ? target.domaines.filter((x) => x !== d) : [...target.domaines, d]
}
const freeDomaineCreate = ref('')
const freeDomaineEdit = ref('')
function addFreeDomaineCreate() {
  const v = freeDomaineCreate.value.trim()
  if (v && !createForm.domaines.includes(v)) createForm.domaines = [...createForm.domaines, v]
  freeDomaineCreate.value = ''
}
function addFreeDomaineEdit() {
  const v = freeDomaineEdit.value.trim()
  if (v && !edit.domaines.includes(v)) edit.domaines = [...edit.domaines, v]
  freeDomaineEdit.value = ''
}

/* Statut */
async function onStatusSelect(event: Event) {
  if (!store.point) return
  const value = (event.target as HTMLSelectElement).value as AttentionPointStatus
  if (!value || value === store.point.status) return
  await store.changeStatus(store.point._id, value)
}

/* Levée */
const resolveComment = ref('')
const resolveDateFin = ref(todayInput())
async function submitResolve() {
  if (!store.point || !resolveComment.value.trim()) return
  await store.resolve(store.point._id, resolveComment.value.trim(), resolveDateFin.value || undefined)
  resolveComment.value = ''
}
async function reopen() {
  if (!store.point) return
  await store.changeStatus(store.point._id, 'OUVERT', 'Réouverture du point')
}

/* Journal */
const journalKind = ref<JournalEntryKind>('ACTION')
const journalMessage = ref('')
async function submitJournal() {
  if (!store.point || !journalMessage.value.trim()) return
  await store.addJournalEntry(store.point._id, journalKind.value, journalMessage.value.trim())
  journalMessage.value = ''
}

/* Navigation */
function openPoint(id: string) {
  router.push(`/admin/pole-dev/points-attention/${id}`)
}
function goBack() {
  store.$patch({ point: null })
  router.push('/admin/pole-dev/points-attention')
}

watch(
  () => route.params.id,
  async (id) => {
    if (id && typeof id === 'string') {
      await store.fetchOne(id)
      if (store.point) {
        loadEditFromPoint(store.point)
        resolveDateFin.value = todayInput()
        resolveComment.value = ''
      }
    } else {
      store.$patch({ point: null })
      await refresh()
    }
  },
  { immediate: true },
)

watch(search, debouncedRefresh)
watch([selectedStatuses, selectedTypes, selectedCriticites, overdueOnly], debouncedRefresh, { deep: true })

onMounted(async () => {
  if (!route.params.id) await refresh()
  if (usersStore.users.length === 0) await usersStore.fetchUsers()
})
</script>

<template>
  <div class="w-full p-6 flex flex-col gap-4">
    <!-- En-tête liste -->
    <template v-if="!store.point">
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div class="flex items-center gap-3">
          <Bs2Title title="Points d'attention" />
          <span v-if="!store.loading" class="text-sm text-base-content/60 mt-1">{{ store.total }} point(s)</span>
        </div>
        <div class="flex gap-2">
          <ButtonDaisy text="Recharger" variant="secondary" :icon="arrowPathIcon" :disabled="store.loading" @click="refresh" />
          <ButtonDaisy text="Nouveau point" variant="primary" :icon="plusIcon" @click="openCreate" />
          <button class="btn btn-sm" :disabled="store.loading" @click="doExport('csv')">CSV</button>
          <button class="btn btn-sm btn-primary" :disabled="store.loading" @click="doExport('xlsx')">XLSX</button>
        </div>
      </div>

      <!-- Compteurs -->
      <div v-if="store.stats" class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Ouverts (à traiter)</div>
          <div class="text-2xl font-semibold">{{ store.stats.ouverts }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">En retard</div>
          <div class="text-2xl font-semibold text-red-600">{{ store.stats.overdue }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Critiques</div>
          <div class="text-2xl font-semibold text-red-600">{{ store.stats.criticiteCounts?.CRITIQUE ?? 0 }}</div>
        </div>
        <div class="card bg-base-100 shadow p-3">
          <div class="text-xs text-base-content/60">Total</div>
          <div class="text-2xl font-semibold">{{ store.stats.total }}</div>
        </div>
      </div>
    </template>

    <template v-else>
      <ButtonDaisy text="Retour" variant="primary" :icon="chevronLeftIcon" @click="goBack" />
    </template>

    <!-- Modal création -->
    <div v-if="showCreate" class="modal modal-open">
      <div class="modal-box max-w-3xl">
        <h3 class="font-semibold mb-3">Nouveau point d'attention</h3>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Type</span>
            <select v-model="createForm.type" class="select select-bordered select-sm">
              <option v-for="t in TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
            </select>
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Criticité</span>
            <select v-model="createForm.criticite" class="select select-bordered select-sm">
              <option v-for="c in CRITICITES" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Référent (interne, facultatif)</span>
            <UserSelect
              :model-value="createForm.responsableUuid"
              placeholder="Rechercher un référent…"
              @update:model-value="createForm.responsableUuid = $event ?? ''"
            />
          </label>
        </div>

        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">Titre</span>
          <input v-model="createForm.title" type="text" class="input input-bordered input-sm w-full" placeholder="Court mais clair" />
        </label>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Date d'entrée</span>
            <input v-model="createForm.dateEntree" type="date" class="input input-bordered input-sm w-full" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Date de début</span>
            <input v-model="createForm.dateDebut" type="date" class="input input-bordered input-sm w-full" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Résolution prévue</span>
            <input v-model="createForm.resolutionPrevue" type="date" class="input input-bordered input-sm w-full" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Alerte « sans suivi » après</span>
            <select v-model.number="createForm.staleThresholdDays" class="select select-bordered select-sm">
              <option v-for="d in THRESHOLDS" :key="d" :value="d">{{ d }} jours</option>
            </select>
          </label>
        </div>

        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">{{ createTypeMeta.descriptionPrompt }}</span>
          <textarea v-model="createForm.description" class="textarea textarea-bordered w-full" rows="3" :placeholder="createTypeMeta.descriptionPrompt" />
        </label>
        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">{{ createTypeMeta.impactPrompt }}</span>
          <textarea v-model="createForm.impact" class="textarea textarea-bordered w-full" rows="3" :placeholder="createTypeMeta.impactPrompt" />
        </label>

        <div class="form-control mb-3">
          <span class="label-text text-xs mb-1">Domaines</span>
          <div class="flex flex-wrap gap-1 mb-2">
            <button
              v-for="d in ATTENTION_DOMAINES_SUGGESTED"
              :key="d"
              type="button"
              class="badge badge-sm cursor-pointer"
              :class="createForm.domaines.includes(d) ? 'badge-primary' : 'badge-outline'"
              @click="toggleDomaine(createForm, d)"
            >
              {{ d }}
            </button>
          </div>
          <div class="flex gap-2">
            <input
              v-model="freeDomaineCreate"
              type="text"
              class="input input-bordered input-xs flex-1"
              placeholder="Ajouter un domaine…"
              @keydown.enter.prevent="addFreeDomaineCreate"
            />
          </div>
          <div v-if="createForm.domaines.length" class="flex flex-wrap gap-1 mt-2">
            <span v-for="d in createForm.domaines" :key="d" class="badge badge-sm badge-primary gap-1">
              {{ d }}
              <button type="button" @click="toggleDomaine(createForm, d)">✕</button>
            </span>
          </div>
        </div>

        <div class="form-control mb-3">
          <span class="label-text text-xs mb-1">Interlocuteurs</span>
          <AttentionInterlocutorsEditor v-model="createForm.interlocuteurs" />
        </div>

        <div class="modal-action">
          <button class="btn btn-sm" @click="showCreate = false">Annuler</button>
          <button class="btn btn-sm btn-primary" :disabled="!canCreate" @click="submitCreate">Créer</button>
        </div>
      </div>
    </div>

    <!-- Détail -->
    <template v-if="store.point">
      <div class="card bg-base-100 shadow p-4">
        <div class="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div class="text-xs text-base-content/60 flex items-center gap-1 flex-wrap">
              <span class="font-mono">{{ store.point.code }}</span> ·
              <span class="inline-flex items-center rounded-full border px-2 py-0.5 font-medium" :class="ATTENTION_TYPE_BY_VALUE[store.point.type]?.color">
                {{ ATTENTION_TYPE_BY_VALUE[store.point.type]?.label }}
              </span>
              <span class="inline-flex items-center rounded-full border px-2 py-0.5 font-medium" :class="ATTENTION_CRITICITE_BY_VALUE[store.point.criticite]?.color">
                {{ ATTENTION_CRITICITE_BY_VALUE[store.point.criticite]?.label }}
              </span>
              <span class="inline-flex items-center rounded-full border px-2 py-0.5 font-medium" :class="ATTENTION_STATUS_BY_VALUE[store.point.status]?.color">
                {{ ATTENTION_STATUS_BY_VALUE[store.point.status]?.label }}
              </span>
              <span v-if="isOverdue(store.point)" class="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-100 text-red-800 px-2 py-0.5 font-medium">
                <ExclamationTriangleIcon class="w-3 h-3" /> En retard
              </span>
              <span
                v-if="isStale(store.point)"
                class="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-100 text-rose-800 px-2 py-0.5 font-medium"
                :title="`Aucune action/commentaire depuis ${daysSinceActivity(store.point)} j (seuil ${store.point.staleThresholdDays} j)`"
              >
                ⏳ Sans suivi {{ store.point.staleThresholdDays }} j
              </span>
            </div>
            <h2 class="text-xl font-semibold mt-1">{{ store.point.title }}</h2>
            <div class="text-sm text-base-content/70 mt-2">Référent : <strong>{{ responsableLabel(store.point) }}</strong></div>
          </div>
          <div class="text-xs text-right text-base-content/60">
            <div>Entrée : {{ formatDate(store.point.dateEntree) }}</div>
            <div>Début : {{ formatDate(store.point.dateDebut) }}</div>
            <div>Résolution prévue : {{ formatDate(store.point.resolutionPrevue) }}</div>
            <div v-if="store.point.dateFin">Fin : {{ formatDate(store.point.dateFin) }}</div>
            <div v-if="store.point.status !== 'LEVE'" :class="isStale(store.point) ? 'text-rose-600 font-semibold' : ''">
              Sans suivi depuis {{ daysSinceActivity(store.point) }} j (seuil {{ store.point.staleThresholdDays }} j)
            </div>
          </div>
        </div>
        <div v-if="store.point.interlocuteurs.length" class="mt-3 flex flex-wrap gap-1">
          <span
            v-for="(i, idx) in store.point.interlocuteurs"
            :key="idx"
            class="badge badge-outline badge-sm"
            :class="i.kind === 'EXTERNE' ? 'badge-warning' : ''"
          >
            {{ i.kind === 'EXTERNE' ? '↗ ' : '' }}{{ interlocutorLabel(i) }}
          </span>
        </div>
      </div>

      <!-- Statut + levée -->
      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Statut</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Statut courant</span>
            <select
              class="select select-bordered select-sm"
              :value="store.point.status"
              :disabled="store.loading || store.point.status === 'LEVE'"
              @change="onStatusSelect"
            >
              <option value="OUVERT">OUVERT</option>
              <option value="EN_COURS">EN COURS</option>
              <option value="LEVE" disabled>LEVÉ (via la levée ci-dessous)</option>
            </select>
          </label>
        </div>

        <!-- Levée du point -->
        <div v-if="store.point.status !== 'LEVE'" class="mt-4 border-t pt-3">
          <h4 class="font-semibold text-sm mb-2">Lever le point</h4>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <label class="form-control md:col-span-2">
              <span class="label-text text-xs mb-1">Commentaire final</span>
              <textarea v-model="resolveComment" class="textarea textarea-bordered w-full" rows="2" placeholder="Comment le point a-t-il été levé ?" />
            </label>
            <label class="form-control">
              <span class="label-text text-xs mb-1">Date de fin du blocage</span>
              <input v-model="resolveDateFin" type="date" class="input input-bordered input-sm w-full" />
            </label>
          </div>
          <button class="btn btn-sm btn-success mt-3" :disabled="store.loading || !resolveComment.trim()" @click="submitResolve">
            Lever le point
          </button>
        </div>
        <div v-else class="mt-4 border-t pt-3">
          <div class="rounded border border-emerald-200 bg-emerald-50 p-3">
            <div class="text-xs text-emerald-800 mb-1">
              Point levé le {{ formatDate(store.point.dateFin) }}
              <span v-if="store.point.resolution"> par {{ [store.point.resolution.firstName, store.point.resolution.lastName].filter(Boolean).join(' ') }}</span>
            </div>
            <div class="text-sm whitespace-pre-wrap">{{ store.point.resolution?.comment }}</div>
          </div>
          <button class="btn btn-sm btn-outline mt-3" :disabled="store.loading" @click="reopen">Rouvrir le point</button>
        </div>

        <h4 class="font-semibold text-sm mt-4 mb-2">Historique</h4>
        <ul class="text-xs text-base-content/70 list-disc list-inside space-y-1">
          <li v-for="(h, i) in store.point.statusHistory" :key="i">
            <strong>{{ ATTENTION_STATUS_BY_VALUE[h.status]?.label ?? h.status }}</strong>
            — {{ formatDateTime(h.at) }}
            <span v-if="h.firstName || h.lastName"> par {{ [h.firstName, h.lastName].filter(Boolean).join(' ') }}</span>
            <span v-if="h.note" class="italic">— {{ h.note }}</span>
          </li>
        </ul>
      </div>

      <!-- Édition -->
      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Détails du point</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Type</span>
            <select v-model="edit.type" class="select select-bordered select-sm">
              <option v-for="t in TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
            </select>
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Criticité</span>
            <select v-model="edit.criticite" class="select select-bordered select-sm">
              <option v-for="c in CRITICITES" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Référent (interne)</span>
            <UserSelect
              :model-value="edit.responsableUuid"
              placeholder="Rechercher un référent…"
              @update:model-value="edit.responsableUuid = $event ?? ''"
            />
          </label>
        </div>

        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">Titre</span>
          <input v-model="edit.title" type="text" class="input input-bordered input-sm w-full" />
        </label>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Date d'entrée</span>
            <input v-model="edit.dateEntree" type="date" class="input input-bordered input-sm w-full" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Date de début</span>
            <input v-model="edit.dateDebut" type="date" class="input input-bordered input-sm w-full" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Résolution prévue</span>
            <input v-model="edit.resolutionPrevue" type="date" class="input input-bordered input-sm w-full" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Alerte « sans suivi » après</span>
            <select v-model.number="edit.staleThresholdDays" class="select select-bordered select-sm">
              <option v-for="d in THRESHOLDS" :key="d" :value="d">{{ d }} jours</option>
            </select>
          </label>
        </div>

        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">{{ editTypeMeta.descriptionPrompt }}</span>
          <textarea v-model="edit.description" class="textarea textarea-bordered w-full" rows="3" />
        </label>
        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">{{ editTypeMeta.impactPrompt }}</span>
          <textarea v-model="edit.impact" class="textarea textarea-bordered w-full" rows="3" />
        </label>

        <div class="form-control mb-3">
          <span class="label-text text-xs mb-1">Domaines</span>
          <div class="flex flex-wrap gap-1 mb-2">
            <button
              v-for="d in ATTENTION_DOMAINES_SUGGESTED"
              :key="d"
              type="button"
              class="badge badge-sm cursor-pointer"
              :class="edit.domaines.includes(d) ? 'badge-primary' : 'badge-outline'"
              @click="toggleDomaine(edit, d)"
            >
              {{ d }}
            </button>
          </div>
          <input
            v-model="freeDomaineEdit"
            type="text"
            class="input input-bordered input-xs w-full"
            placeholder="Ajouter un domaine…"
            @keydown.enter.prevent="addFreeDomaineEdit"
          />
          <div v-if="edit.domaines.length" class="flex flex-wrap gap-1 mt-2">
            <span v-for="d in edit.domaines" :key="d" class="badge badge-sm badge-primary gap-1">
              {{ d }}
              <button type="button" @click="toggleDomaine(edit, d)">✕</button>
            </span>
          </div>
        </div>

        <div class="form-control mb-3">
          <span class="label-text text-xs mb-1">Interlocuteurs</span>
          <AttentionInterlocutorsEditor v-model="edit.interlocuteurs" />
        </div>

        <button class="btn btn-sm btn-primary self-start" :disabled="store.loading || !editValid" @click="saveDetails">
          Sauvegarder
        </button>
      </div>

      <!-- Journal -->
      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Journal (commentaires, détails, actions)</h3>
        <div v-if="store.point.journal.length === 0" class="text-sm text-base-content/60">Aucune entrée.</div>
        <div v-else class="flex flex-col gap-3">
          <div
            v-for="entry in [...store.point.journal].reverse()"
            :key="entry.id"
            class="rounded border border-base-300 p-3 bg-base-100"
          >
            <div class="flex justify-between items-center text-xs mb-1">
              <span class="inline-flex items-center rounded-full border px-2 py-0.5 font-medium" :class="JOURNAL_KIND_BY_VALUE[entry.kind]?.color">
                {{ JOURNAL_KIND_BY_VALUE[entry.kind]?.label }}
              </span>
              <span class="text-base-content/60">
                {{ [entry.firstName, entry.lastName].filter(Boolean).join(' ') }} · {{ formatDateTime(entry.at) }}
              </span>
            </div>
            <div class="text-sm whitespace-pre-wrap">{{ entry.message }}</div>
          </div>
        </div>

        <div class="mt-4 pt-3 border-t flex flex-col gap-2">
          <div class="flex gap-2 items-center">
            <select v-model="journalKind" class="select select-bordered select-sm">
              <option v-for="k in JOURNAL_KINDS" :key="k.value" :value="k.value">{{ k.label }}</option>
            </select>
            <span class="text-xs text-base-content/50">Horodaté automatiquement à l'ajout</span>
          </div>
          <textarea v-model="journalMessage" class="textarea textarea-bordered w-full" rows="3" placeholder="Nouvelle entrée…" />
          <button class="btn btn-sm btn-primary self-end" :disabled="store.loading || !journalMessage.trim()" @click="submitJournal">
            Ajouter
          </button>
        </div>
      </div>
    </template>

    <!-- Liste -->
    <template v-else>
      <div class="card bg-base-100 shadow p-4">
        <div class="flex flex-wrap gap-4 mb-4">
          <label class="form-control flex-1 min-w-[200px]">
            <span class="label-text text-xs mb-1">Rechercher</span>
            <input v-model="search" type="text" placeholder="Code, titre, description, impact…" class="input input-bordered input-sm w-full" />
          </label>

          <div class="form-control">
            <span class="label-text text-xs mb-1">Statut</span>
            <div class="flex gap-1 flex-wrap">
              <label
                v-for="s in STATUSES"
                :key="s.value"
                class="cursor-pointer flex items-center gap-1 border rounded px-2 py-1 text-xs"
                :class="selectedStatuses.includes(s.value) ? s.color : 'opacity-50'"
              >
                <input type="checkbox" :value="s.value" v-model="selectedStatuses" class="checkbox checkbox-xs" />
                {{ s.label }}
              </label>
            </div>
          </div>

          <div class="form-control">
            <span class="label-text text-xs mb-1">Type</span>
            <div class="flex gap-1 flex-wrap">
              <label
                v-for="t in TYPES"
                :key="t.value"
                class="cursor-pointer flex items-center gap-1 border rounded px-2 py-1 text-xs"
                :class="selectedTypes.includes(t.value) ? t.color : 'opacity-50'"
              >
                <input type="checkbox" :value="t.value" v-model="selectedTypes" class="checkbox checkbox-xs" />
                {{ t.label }}
              </label>
            </div>
          </div>

          <div class="form-control">
            <span class="label-text text-xs mb-1">Criticité</span>
            <div class="flex gap-1 flex-wrap">
              <label
                v-for="c in CRITICITES"
                :key="c.value"
                class="cursor-pointer flex items-center gap-1 border rounded px-2 py-1 text-xs"
                :class="selectedCriticites.includes(c.value) ? c.color : 'opacity-50'"
              >
                <input type="checkbox" :value="c.value" v-model="selectedCriticites" class="checkbox checkbox-xs" />
                {{ c.label }}
              </label>
            </div>
          </div>

          <label class="form-control justify-end">
            <span class="cursor-pointer flex items-center gap-2 text-xs">
              <input type="checkbox" v-model="overdueOnly" class="checkbox checkbox-xs" />
              En retard uniquement
            </span>
          </label>
        </div>

        <div v-if="store.loading" class="flex justify-center py-12">
          <ArrowPathIcon class="w-8 h-8 animate-spin text-base-content/40" />
        </div>

        <div v-else-if="store.points.length === 0" class="flex flex-col items-center py-12 gap-2 text-base-content/60">
          <InboxIcon class="w-12 h-12" />
          <p>Aucun point d'attention</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Code</th>
                <th>Titre</th>
                <th>Type</th>
                <th>Criticité</th>
                <th>Statut</th>
                <th>Suivi</th>
                <th>Référent</th>
                <th>Résol. prévue</th>
                <th>Entrée</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in store.points" :key="p._id" class="cursor-pointer hover:bg-base-200" @click="openPoint(p._id)">
                <td class="font-mono text-xs">{{ p.code }}</td>
                <td class="max-w-md truncate">{{ p.title }}</td>
                <td>
                  <span class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium" :class="ATTENTION_TYPE_BY_VALUE[p.type]?.color">
                    {{ ATTENTION_TYPE_BY_VALUE[p.type]?.label }}
                  </span>
                </td>
                <td>
                  <span class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium" :class="ATTENTION_CRITICITE_BY_VALUE[p.criticite]?.color">
                    {{ ATTENTION_CRITICITE_BY_VALUE[p.criticite]?.label }}
                  </span>
                </td>
                <td>
                  <span class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium" :class="ATTENTION_STATUS_BY_VALUE[p.status]?.color">
                    {{ ATTENTION_STATUS_BY_VALUE[p.status]?.label }}
                  </span>
                </td>
                <td>
                  <span
                    v-if="isStale(p)"
                    class="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-100 text-rose-800 px-2 py-0.5 text-xs font-medium"
                    :title="`Aucune action/commentaire depuis ${daysSinceActivity(p)} j (seuil ${p.staleThresholdDays} j)`"
                  >
                    ⏳ {{ p.staleThresholdDays }} j
                  </span>
                  <span v-else class="text-xs text-base-content/40">—</span>
                </td>
                <td>{{ responsableLabel(p) }}</td>
                <td class="text-xs" :class="isOverdue(p) ? 'text-red-600 font-semibold' : 'text-base-content/60'">
                  {{ formatDate(p.resolutionPrevue) }}
                  <span v-if="isOverdue(p)" title="En retard">⚠</span>
                </td>
                <td class="text-xs text-base-content/60">{{ formatDate(p.dateEntree) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
